import type { IntelligenceApi } from "./api";
import { AuthenticationRequiredError } from "./auth";
import type { AnalysisModelId, AreaGeography, MapArea, MapAreasResponse } from "./domain";
import { areaGeographyLabel, formatPercentile } from "./presentation";

interface RankingContext {
  readonly model: AnalysisModelId;
  readonly geography: AreaGeography;
  readonly region: string;
  readonly metric: string;
  readonly percentiles: readonly (number | null)[];
}

export interface RankedArea extends MapArea {
  readonly percentile: number;
}

export function rankAreas(
  areas: readonly MapArea[],
  percentiles: readonly (number | null)[],
): RankedArea[] {
  if (
    areas.length !== percentiles.length ||
    areas.some(({ index }) => index >= percentiles.length)
  ) {
    throw new Error("The area list does not match the displayed analysis.");
  }
  return areas
    .flatMap((area) => {
      const percentile = percentiles[area.index];
      return percentile === null || percentile === undefined ? [] : [{ ...area, percentile }];
    })
    .sort((a, b) => b.percentile - a.percentile || a.code.localeCompare(b.code));
}

const PAGE_SIZE = 50;

export class RankedAreasPanel {
  private context: RankingContext | null = null;
  private readonly cache = new Map<string, MapAreasResponse>();
  private request: AbortController | null = null;
  private rows: readonly RankedArea[] = [];
  private page = 0;
  private destroyed = false;

  public constructor(
    private readonly root: HTMLElement,
    private readonly api: Pick<IntelligenceApi, "mapAreas">,
    private readonly onSelect: (area: MapArea) => void,
    private readonly onAuthenticationRequired: (error: AuthenticationRequiredError) => void,
  ) {}

  public setVisible(visible: boolean): void {
    this.root.hidden = !visible;
    if (visible) void this.load();
    else this.request?.abort();
  }

  public update(context: RankingContext): void {
    this.context = context;
    if (!this.root.hidden) void this.load();
  }

  public destroy(): void {
    this.destroyed = true;
    this.request?.abort();
  }

  private async load(): Promise<void> {
    this.request?.abort();
    const context = this.context;
    if (context === null || this.destroyed) return;
    const request = new AbortController();
    this.request = request;
    this.page = 0;
    this.root.setAttribute("aria-busy", "true");
    this.root.replaceChildren(
      sidebarContent(this.heading(context), paragraph("Loading areas…")),
    );
    try {
      const key = `${context.model}:${context.geography}`;
      const result =
        this.cache.get(key) ??
        (await this.api.mapAreas(context.model, context.geography, request.signal));
      if (request.signal.aborted || this.destroyed) return;
      this.rows = rankAreas(result.areas, context.percentiles);
      this.cache.set(key, result);
      this.render();
    } catch (error) {
      if (request.signal.aborted || this.destroyed) return;
      if (error instanceof AuthenticationRequiredError) {
        this.onAuthenticationRequired(error);
        return;
      }
      const message = paragraph(error instanceof Error ? error.message : String(error));
      message.setAttribute("role", "status");
      const retry = button("Retry", () => {
        void this.load();
      });
      this.root.replaceChildren(sidebarContent(this.heading(context), message, retry));
    } finally {
      if (this.request === request) this.root.setAttribute("aria-busy", "false");
    }
  }

  private heading(context: RankingContext): HTMLElement {
    const heading = document.createElement("div");
    heading.className = "ranking-heading";
    const title = document.createElement("h2");
    title.textContent = "Ranked areas";
    const detail = paragraph(`${context.region} · ${areaGeographyLabel(context.geography)}`);
    detail.className = "ranking-region";
    const metric = paragraph(`${context.metric} · highest to lowest`);
    heading.append(title, detail, metric);
    return heading;
  }

  private render(): void {
    if (this.context === null) return;
    const content = sidebarContent(this.heading(this.context));
    this.root.replaceChildren(content);
    if (this.rows.length === 0) {
      content.append(paragraph("No areas have a percentile for this metric."));
      return;
    }
    const start = this.page * PAGE_SIZE;
    const list = document.createElement("ol");
    list.className = "ranking-list";
    list.start = start + 1;
    for (const area of this.rows.slice(start, start + PAGE_SIZE)) {
      const item = document.createElement("li");
      const select = document.createElement("button");
      select.type = "button";
      select.className = "ranking-area";
      const name = document.createElement("span");
      name.textContent =
        area.name && area.name !== area.code ? `${area.name} · ${area.code}` : area.code;
      const percentile = document.createElement("strong");
      percentile.textContent = formatPercentile(area.percentile);
      select.setAttribute(
        "aria-label",
        `${name.textContent}, percentile ${percentile.textContent}. Show area on map`,
      );
      select.append(name, percentile);
      select.addEventListener("click", () => this.onSelect(area));
      item.append(select);
      list.append(item);
    }
    const navigation = document.createElement("nav");
    navigation.className = "ranking-navigation";
    navigation.setAttribute("aria-label", "Ranked area pages");
    const lastPage = Math.ceil(this.rows.length / PAGE_SIZE) - 1;
    const count = paragraph(
      `${start + 1}–${Math.min(start + PAGE_SIZE, this.rows.length)} of ${this.rows.length.toLocaleString()} · percentile`,
    );
    count.setAttribute("role", "status");
    navigation.append(count);
    for (const [label, page] of [
      ["Highest", 0],
      ["Previous", this.page - 1],
      ["Next", this.page + 1],
      ["Lowest", lastPage],
    ] as const) {
      const control = button(label, () => {
        this.page = page;
        this.render();
        this.root.scrollTop = 0;
        this.root.querySelector<HTMLButtonElement>(".ranking-area")?.focus();
      });
      control.disabled = page < 0 || page > lastPage || page === this.page;
      navigation.append(control);
    }
    content.append(navigation, list);
  }
}

function sidebarContent(...children: HTMLElement[]): HTMLDivElement {
  const result = document.createElement("div");
  result.className = "sidebar-content";
  result.append(...children);
  return result;
}

function paragraph(text: string): HTMLParagraphElement {
  const result = document.createElement("p");
  result.className = "ranking-context";
  result.textContent = text;
  return result;
}

function button(label: string, action: () => void): HTMLButtonElement {
  const result = document.createElement("button");
  result.type = "button";
  result.className = "button button--white";
  result.textContent = label;
  result.addEventListener("click", action);
  return result;
}
